import { useQuery } from '@tanstack/react-query';
import useSearchMutaion from '../../../hooks/apis/useSearchMutaion';
import { apiRequest } from '../../../Services/Common/ApiRequest';
import Global from '../../../Services/Common/Global';
import useSaveMutation from '../../../hooks/apis/useSaveMutaion';
import useDeleteMutation from '../../../hooks/apis/useDeleteMutaion';
import { mapParamsBySchema, mapRowsBySchema } from '../../../util';
import { normalizePopBasicLinkUrl } from './POP_BASIC_FileUtil';
import {
  POP_BASIC_Schema,
  POP_BASIC_DISPLAY_LAYER,
  POP_BASIC_TARGET_TYPE,
  getPopBasicDisplayLayerLabel,
  getPopBasicTargetTypeLabel,
  getPopBasicTypeLabel,
  parsePopBasicList,
} from './POP_BASIC_Schema';

const ynToDisplay = (value) =>
  value === true || value === 'Y' || value === 'y' ? 'Y' : 'N';

/** GetPopBasicR1 / R2 응답 정규화 */
export const normalizePopBasicR1Row = (r, rowIndex = 0) => {
  const popId = r.popId ?? r.PopId ?? r.POP_ID ?? '';
  const popType = r.popType ?? r.PopType ?? r.POP_TYPE ?? 'GENERAL';
  const targetType =
    r.targetType ?? r.TargetType ?? r.TARGET_TYPE ?? POP_BASIC_TARGET_TYPE;
  const displayLayer =
    r.displayLayer ?? r.DisplayLayer ?? r.DISPLAY_LAYER ?? POP_BASIC_DISPLAY_LAYER;

  return {
    no: r.no ?? r.No ?? r.Seq ?? r.seq ?? `pop:${popId}:${rowIndex}`,
    popId,
    coCd: r.coCd ?? r.CoCd ?? r.CO_CD ?? '',
    popType,
    popTypeNm: r.popTypeNm ?? r.PopTypeNm ?? getPopBasicTypeLabel(popType),
    title: r.title ?? r.Title ?? r.TITLE ?? '',
    content: r.content ?? r.Content ?? r.CONTENT ?? '',
    targetType,
    targetTypeNm:
      r.targetTypeNm ??
      r.TargetTypeNm ??
      getPopBasicTargetTypeLabel(targetType),
    targetIndCds: parsePopBasicList(
      r.targetIndCds ?? r.TargetIndCds ?? r.TARGET_INDUSTRY_CDS
    ),
    targetCoCds: parsePopBasicList(
      r.targetCoCds ?? r.TargetCoCds ?? r.TARGET_CO_CDS
    ),
    targetIndNms: r.targetIndNms ?? r.TargetIndNms ?? r.TARGET_INDUSTRY_NMS ?? '',
    targetCoNms: r.targetCoNms ?? r.TargetCoNms ?? r.TARGET_CO_NMS ?? '',
    dispStartDt: r.dispStartDt ?? r.DispStartDt ?? r.DISP_START_DT ?? null,
    dispEndDt: r.dispEndDt ?? r.DispEndDt ?? r.DISP_END_DT ?? null,
    dispPriority: Number(r.dispPriority ?? r.DispPriority ?? r.DISP_PRIORITY ?? 1),
    isForceClose: ynToDisplay(r.isForceClose ?? r.IsForceClose ?? r.IS_FORCE_CLOSE),
    isHideToday: ynToDisplay(r.isHideToday ?? r.IsHideToday ?? r.IS_HIDE_TODAY),
    displayLayer,
    displayLayerNm:
      r.displayLayerNm ??
      r.DisplayLayerNm ??
      getPopBasicDisplayLayerLabel(displayLayer),
    linkUrl: normalizePopBasicLinkUrl(
      r.linkUrl ?? r.LinkUrl ?? r.LINK_URL ?? ''
    ),
    adminMemo: r.adminMemo ?? r.AdminMemo ?? r.ADMIN_MEMO ?? '',
    useYn: ynToDisplay(r.useYn ?? r.UseYn ?? r.USE_YN ?? 'Y'),
    createId: r.createId ?? r.CreateId ?? r.CREATE_ID ?? '',
    createDate: r.createDate ?? r.CreateDate ?? r.CREATE_DATE ?? '',
    updateId: r.updateId ?? r.UpdateId ?? r.UPDATE_ID ?? '',
    updateDate: r.updateDate ?? r.UpdateDate ?? r.UPDATE_DATE ?? '',
  };
};

/** GetPopBasicR3 파일 목록 정규화 */
export const normalizePopBasicFileRow = (r, rowIndex = 0) => {
  const popFileId = r.popFileId ?? r.PopFileId ?? r.POP_FILE_ID ?? '';
  const popId = r.popId ?? r.PopId ?? r.POP_ID ?? '';
  const fileType = r.fileType ?? r.FileType ?? r.FILE_TYPE ?? 'ATTACH';
  const filePath = r.filePath ?? r.FilePath ?? r.FILE_PATH ?? '';
  return {
    no: r.no ?? r.No ?? `popfile:${popFileId}:${rowIndex}`,
    popFileId,
    popId,
    coCd: r.coCd ?? r.CoCd ?? r.CO_CD ?? '',
    fileType,
    fileName: r.fileName ?? r.FileName ?? r.FILE_NAME ?? '',
    saveName: r.saveName ?? r.SaveName ?? r.SAVE_NAME ?? '',
    filePath,
    fileSize: Number(r.fileSize ?? r.FileSize ?? r.FILE_SIZE ?? 0),
    sortOrder: Number(r.sortOrder ?? r.SortOrder ?? r.SORT_ORDER ?? 0),
    useYn: ynToDisplay(r.useYn ?? r.UseYn ?? r.USE_YN ?? 'Y'),
    preview: filePath ? `${Global.file.useUrl}files/${filePath}` : '',
    previewFilePath: '',
    deleted: false,
    createId: r.createId ?? r.CreateId ?? r.CREATE_ID ?? '',
    createDate: r.createDate ?? r.CreateDate ?? r.CREATE_DATE ?? '',
  };
};

export const normalizePopBasicBatchResults = (res) => {
  let list = [];
  if (Array.isArray(res)) list = res;
  else if (Array.isArray(res?.data)) list = res.data;
  else if (Array.isArray(res?.Data)) list = res.Data;
  else if (res && typeof res === 'object') {
    if ('isSuccess' in res || 'IsSuccess' in res) list = [{ ...res }];
  }
  return list.map((item) => ({
    ...item,
    isSuccess: item.isSuccess ?? item.IsSuccess ?? false,
    message: item.message ?? item.Message ?? item.errorMsg ?? item.ErrorMsg ?? '',
  }));
};

const extractRows = (raw) => {
  if (raw?.isSuccess === false || raw?.IsSuccess === false) return [];
  if (Array.isArray(raw?.data)) return raw.data;
  if (Array.isArray(raw?.Data)) return raw.Data;
  return [];
};

/** 팝업 목록 GetPopBasicR1 — run({ userInfo, search }) */
export const useSearch1 = (successOptions) => {
  return useSearchMutaion(async (context) => {
    const params = mapParamsBySchema(POP_BASIC_Schema.mapper.search1, {
      userInfo: context.userInfo,
      search: context.search,
    });
    const res = await apiRequest(
      'post',
      Global.urls.PopupManagement.getPopBasicR1,
      params
    );
    const arr = extractRows(res ?? {});
    return arr.map((row, idx) => normalizePopBasicR1Row(row, idx));
  }, successOptions);
};

/** 대시보드 노출용 GetPopBasicR2 */
export const useSearch2 = (options = {}) => {
  const { userInfo } = options;
  const coCd = userInfo?.coCd;
  const userid = userInfo?.userid;

  return useQuery({
    queryKey: ['PopupManagement', 'getPopBasicR2', coCd, userid],
    queryFn: async () => {
      const params = mapParamsBySchema(POP_BASIC_Schema.mapper.search2, {
        userInfo,
      });
      return apiRequest(
        'post',
        Global.urls.PopupManagement.getPopBasicR2,
        params
      );
    },
    select: (raw) =>
      extractRows(raw ?? {}).map((row, idx) => normalizePopBasicR1Row(row, idx)),
    enabled: Boolean(userid),
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });
};

/** 팝업 파일 목록 GetPopBasicR3 — run({ row: { popId }, fileType }) */
export const useSearch3 = (successOptions) => {
  return useSearchMutaion(async (context) => {
    const params = mapParamsBySchema(POP_BASIC_Schema.mapper.search3, context);
    const res = await apiRequest(
      'post',
      Global.urls.PopupManagement.getPopBasicR3,
      params
    );
    const arr = extractRows(res ?? {});
    return arr.map((row, idx) => normalizePopBasicFileRow(row, idx));
  }, successOptions);
};

/** 팝업 저장 SavePopBasicU1 */
export const useSave1 = (context = {}, options = {}) => {
  return useSaveMutation(async (rows) => {
    const payload = mapRowsBySchema(
      rows,
      POP_BASIC_Schema.mapper.save1,
      context
    );
    const res = await apiRequest(
      'post1',
      Global.urls.PopupManagement.savePopBasicU1,
      payload
    );
    return normalizePopBasicBatchResults(res);
  }, options);
};

/** 팝업 파일 저장 SavePopBasicU2 (NOT_BASIC_01_02 useSave2와 동일 — context는 mapRowsBySchema 기본값) */
export const useSave2 = (options = {}) => {
  return useSaveMutation(async (rows) => {
    const payload = mapRowsBySchema(rows, POP_BASIC_Schema.mapper.save2);
    const res = await apiRequest(
      'post1',
      Global.urls.PopupManagement.savePopBasicU2,
      payload
    );
    return normalizePopBasicBatchResults(res);
  }, options);
};

/** 팝업 삭제 DeletePopBasicD1 */
export const useDelete1 = (options = {}) => {
  return useDeleteMutation(async (rows) => {
    const payload = mapRowsBySchema(rows, POP_BASIC_Schema.mapper.delete1);
    const res = await apiRequest(
      'post1',
      Global.urls.PopupManagement.deletePopBasicD1,
      payload
    );
    return normalizePopBasicBatchResults(res);
  }, options);
};

/** 팝업 동의·숨김 SavePopBasicU3 → TB_POP_POPUP_AGREE */
export const useSave3 = (context = {}, options = {}) => {
  return useSaveMutation(
    async (rows) => {
      const payload = mapRowsBySchema(
        rows,
        POP_BASIC_Schema.mapper.save3,
        context
      );
      const res = await apiRequest(
        'post1',
        Global.urls.PopupManagement.savePopBasicU3,
        payload
      );
      return normalizePopBasicBatchResults(res);
    },
    options,
    false
  );
};

/** 팝업 파일 삭제 DeletePopBasicD2 */
export const useDelete2 = (options = {}) => {
  return useDeleteMutation(async (rows) => {
    const payload = mapRowsBySchema(rows, POP_BASIC_Schema.mapper.delete2);
    const res = await apiRequest(
      'post1',
      Global.urls.PopupManagement.deletePopBasicD2,
      payload
    );
    return normalizePopBasicBatchResults(res);
  }, options);
};
